import { ButtonBase, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { EmulatorType } from '@localstack/types';
import { EMULATOR_LABELS, TEST_IDS } from '@localstack/constants';
import { ReactElement, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { EmulatorIcon } from '../EmulatorIcon';

type Size = 'compact' | 'default';

const ICON_SIZE: Record<Size, number> = {
  compact: 18,
  default: 20,
};

const TRACK_PADDING = 2;

const Track = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'inline-grid',
  gridAutoFlow: 'column',
  gridAutoColumns: 'max-content',
  padding: TRACK_PADDING,
  borderRadius: 6,
  backgroundColor: theme.palette.action.hover,
}));

const Indicator = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: TRACK_PADDING,
  bottom: TRACK_PADDING,
  left: 0,
  borderRadius: 4,
  backgroundColor: theme.palette.background.paper,
  transition: theme.transitions.create(['transform', 'width'], {
    duration: theme.transitions.duration.short,
    easing: theme.transitions.easing.easeInOut,
  }),
}));

const Segment = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'size',
})<{ selected: boolean; size: Size }>(({ theme, selected, size }) => ({
  position: 'relative',
  gap: theme.spacing(0.5),
  padding: size === 'compact' ? theme.spacing(0.375, 1.25) : theme.spacing(0.5, 1.5),
  borderRadius: 4,
  whiteSpace: 'nowrap',
  color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
  transition: theme.transitions.create('color', {
    duration: theme.transitions.duration.short,
  }),
}));

type Props = {
  emulators: readonly EmulatorType[];
  value: EmulatorType;
  onChange: (emulator: EmulatorType) => void;
  size?: Size;
};

export const EmulatorToggle = ({ emulators, value, onChange, size = 'default' }: Props): ReactElement => {
  const trackRef = useRef<HTMLDivElement>(null);
  const segmentRefs = useRef<Partial<Record<EmulatorType, HTMLButtonElement>>>({});
  const [indicator, setIndicator] = useState<Optional<{ left: number; width: number }>>(undefined);

  const measure = useCallback(() => {
    const segment = segmentRefs.current[value];
    const track = trackRef.current;

    if (!segment || !track) return;

    const segmentRect = segment.getBoundingClientRect();
    const left = segmentRect.left - track.getBoundingClientRect().left;
    const width = segmentRect.width;

    setIndicator((prev) => (prev?.left === left && prev?.width === width ? prev : { left, width }));
  }, [value]);

  useLayoutEffect(() => {
    measure();

    const observer = new ResizeObserver(measure);
    Object.values(segmentRefs.current).forEach((segment) => segment && observer.observe(segment));

    return () => observer.disconnect();
  }, [measure, emulators]);

  return (
    <Track ref={trackRef} role="group" aria-label="Emulator" data-testid={TEST_IDS.EMULATOR_TOGGLE}>
      <Indicator
        style={{
          width: indicator?.width ?? 0,
          transform: `translateX(${indicator?.left ?? 0}px)`,
          visibility: indicator ? 'visible' : 'hidden',
        }}
      />
      {emulators.map((emulator) => (
        <Segment
          key={emulator}
          ref={(node) => {
            segmentRefs.current[emulator] = node ?? undefined;
          }}
          data-testid={TEST_IDS.EMULATOR_TOGGLE_SEGMENT(emulator)}
          data-selected={emulator === value}
          disableRipple
          selected={emulator === value}
          size={size}
          aria-pressed={emulator === value}
          onClick={() => onChange(emulator)}
        >
          <EmulatorIcon emulator={emulator} sx={{ fontSize: ICON_SIZE[size] }} />
          <Typography variant="body2" fontWeight={600} fontSize={size === 'compact' ? 13 : undefined}>
            {EMULATOR_LABELS[emulator]}
          </Typography>
        </Segment>
      ))}
    </Track>
  );
};
